import React, { useMemo, useState } from 'react'
import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
    makeStyles,
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import DeleteIcon from '@material-ui/icons/Delete'
import { toast } from 'react-toastify'

import { Organization } from '../../../models'
import { VendorPerformanceTable } from '../../VendorPerformance/VendorPerformanceTable'
import { GenerateVendorPerformanceReportDialog } from './GenerateVendorPerformanceReportDialog'
import {
    VendorPerformanceReport,
    useVendorPerformanceReport,
    useVendorPerformanceReportStatus,
    useVendorPerformanceReports,
} from '../../../hooks/useVendorPerformanceReports'

const useStyles = makeStyles((theme) => ({
    toolbar: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
        flexWrap: 'wrap',
    },
    titleBlock: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0,
    },
    table: {
        minWidth: 720,
    },
    clickableRow: {
        cursor: 'pointer',
    },
    empty: {
        color: theme.palette.text.secondary,
        padding: theme.spacing(3, 0),
    },
    loading: {
        display: 'flex',
        justifyContent: 'center',
        padding: theme.spacing(4),
    },
}))

interface Props {
    organizations: Organization[]
}

const formatDate = (value: string | null) => {
    if (!value) {
        return null
    }
    return value.slice(0, 10)
}

const formatDateTime = (value: string | null) => {
    if (!value) {
        return '—'
    }
    const parsed = new Date(value)
    if (Number.isNaN(parsed.getTime())) {
        return value
    }
    return parsed.toLocaleString()
}

const formatWindow = (report: VendorPerformanceReport) => {
    const start = formatDate(report.start_date)
    const end = formatDate(report.end_date)
    if (!start && !end) {
        return 'All time'
    }
    if (start && end) {
        return `${start} – ${end}`
    }
    return start || end || 'All time'
}

const formatOrganizations = (report: VendorPerformanceReport) => {
    if (report.include_all_organizations) {
        return 'All organizations'
    }
    if (report.organizations.length === 0) {
        return '—'
    }
    if (report.organizations.length === 1) {
        return report.organizations[0].name
    }
    return `${report.organizations[0].name} +${report.organizations.length - 1}`
}

const formatStatus = (status: VendorPerformanceReport['status']) => {
    if (status === 'PENDING') {
        return 'Pending'
    }
    if (status === 'RUNNING') {
        return 'Running'
    }
    if (status === 'COMPLETE') {
        return 'Complete'
    }
    return 'Failed'
}

const organizationTooltip = (report: VendorPerformanceReport) => {
    if (report.include_all_organizations) {
        return 'All organizations'
    }
    return report.organizations
        .map((organization) => organization.name)
        .join(', ')
}

const isActiveStatus = (status: VendorPerformanceReport['status']) =>
    status === 'PENDING' || status === 'RUNNING'

const formatLiveStatus = (report: VendorPerformanceReport) => {
    const label = formatStatus(report.status)
    if (isActiveStatus(report.status) && report.progress_total > 0) {
        return `${label} · ${report.progress_current} of ${report.progress_total} orgs`
    }
    return label
}

export const AdminVendorAnalytics = ({ organizations }: Props) => {
    const classes = useStyles()
    const [generateOpen, setGenerateOpen] = useState(false)
    const [selectedReportId, setSelectedReportId] = useState<number | null>(
        null,
    )
    const {
        reports,
        isLoading,
        isError,
        deleteReport,
    } = useVendorPerformanceReports(true)

    const activeReport = useMemo(
        () =>
            reports.find(
                (report) =>
                    report.status === 'PENDING' || report.status === 'RUNNING',
            ) ?? null,
        [reports],
    )
    useVendorPerformanceReportStatus(activeReport?.id ?? null, true)

    if (selectedReportId != null) {
        return (
            <AdminVendorAnalyticsDetail
                reportId={selectedReportId}
                onBack={() => setSelectedReportId(null)}
            />
        )
    }

    return (
        <>
            <div className={classes.toolbar}>
                <Typography variant="h6">Vendor analytics reports</Typography>
                <Tooltip
                    title={
                        activeReport
                            ? 'A report is already generating'
                            : 'Generate a new report'
                    }
                >
                    <span>
                        <Button
                            color="primary"
                            variant="contained"
                            disabled={!!activeReport}
                            onClick={() => setGenerateOpen(true)}
                        >
                            Generate report
                        </Button>
                    </span>
                </Tooltip>
            </div>

            {activeReport && (
                <Typography color="textSecondary" gutterBottom>
                    Generating report {activeReport.id}
                    {activeReport.progress_total
                        ? ` · ${activeReport.progress_current} of ${activeReport.progress_total} organizations`
                        : ''}
                </Typography>
            )}

            {isLoading ? (
                <Box className={classes.loading}>
                    <CircularProgress />
                </Box>
            ) : isError ? (
                <Typography color="error">
                    Unable to load vendor analytics reports.
                </Typography>
            ) : reports.length === 0 ? (
                <Typography className={classes.empty}>
                    No reports yet. Generate one to compare vendors across
                    organizations.
                </Typography>
            ) : (
                <TableContainer component={Paper}>
                    <Table className={classes.table} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Date window</TableCell>
                                <TableCell>Organizations</TableCell>
                                <TableCell>Generated</TableCell>
                                <TableCell>By</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Vendors</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reports.map((report) => {
                                const isActive = isActiveStatus(report.status)
                                const canOpen = report.status === 'COMPLETE'
                                return (
                                    <TableRow
                                        key={report.id}
                                        hover={canOpen}
                                        className={
                                            canOpen ? classes.clickableRow : ''
                                        }
                                        onClick={() => {
                                            if (canOpen) {
                                                setSelectedReportId(report.id)
                                            }
                                        }}
                                    >
                                        <TableCell>
                                            {formatWindow(report)}
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip
                                                title={organizationTooltip(
                                                    report,
                                                )}
                                            >
                                                <span>
                                                    {formatOrganizations(
                                                        report,
                                                    )}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell>
                                            {formatDateTime(
                                                report.completed_date ||
                                                    report.created_date,
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {report.generated_by_name || '—'}
                                        </TableCell>
                                        <TableCell>
                                            {formatLiveStatus(report)}
                                        </TableCell>
                                        <TableCell align="right">
                                            {isActive ? (
                                                <Tooltip title="Vendor count is written when the report finishes">
                                                    <span>—</span>
                                                </Tooltip>
                                            ) : (
                                                report.row_count
                                            )}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            onClick={(event) =>
                                                event.stopPropagation()
                                            }
                                        >
                                            {!isActive && (
                                                <IconButton
                                                    size="small"
                                                    aria-label="Delete report"
                                                    onClick={async () => {
                                                        try {
                                                            await deleteReport(
                                                                report.id,
                                                            )
                                                        } catch (err: any) {
                                                            toast.error(
                                                                err?.response
                                                                    ?.data
                                                                    ?.message ||
                                                                    'Unable to delete report.',
                                                            )
                                                        }
                                                    }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <GenerateVendorPerformanceReportDialog
                open={generateOpen}
                onClose={() => setGenerateOpen(false)}
                organizations={organizations}
            />
        </>
    )
}

const AdminVendorAnalyticsDetail = ({
    reportId,
    onBack,
}: {
    reportId: number
    onBack: () => void
}) => {
    const classes = useStyles()
    const { report, rows, isLoading, isError } = useVendorPerformanceReport(
        reportId,
        true,
    )

    return (
        <>
            <div className={classes.toolbar}>
                <div className={classes.titleBlock}>
                    <IconButton onClick={onBack} aria-label="Back to reports">
                        <ArrowBackIcon />
                    </IconButton>
                    <div>
                        <Typography variant="h6">
                            {report ? formatWindow(report) : 'Report'}
                        </Typography>
                        {report && (
                            <Typography variant="body2" color="textSecondary">
                                {formatOrganizations(report)}
                                {' · Generated '}
                                {formatDateTime(
                                    report.completed_date ||
                                        report.created_date,
                                )}
                            </Typography>
                        )}
                    </div>
                </div>
            </div>
            {isLoading ? (
                <Box className={classes.loading}>
                    <CircularProgress />
                </Box>
            ) : isError || !report ? (
                <Typography color="error">
                    Unable to load this report.
                </Typography>
            ) : (
                <VendorPerformanceTable
                    rows={rows}
                    isLoading={false}
                    isError={false}
                />
            )}
        </>
    )
}
